import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
const privacyContent = `
Última actualización: febrero 2026

1. Información que recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra: nombre, correo electrónico y datos de pago. No almacenamos datos de tarjetas de crédito directamente; estos son procesados por nuestro proveedor de pagos seguro.

2. Uso de la información
Tu información se utiliza exclusivamente para:
• Procesar y entregar tu compra digital
• Enviarte el producto adquirido
• Comunicarnos contigo sobre tu pedido

3. Protección de datos
Implementamos medidas de seguridad estándar de la industria para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. Cookies
Nuestro sitio puede utilizar cookies esenciales para el funcionamiento del sitio y cookies analíticas para mejorar la experiencia del usuario.

5. Terceros
No vendemos, intercambiamos ni transferimos tu información personal a terceros, excepto a proveedores de servicios necesarios para completar tu transacción.

6. Tus derechos
Puedes solicitar acceso, corrección o eliminación de tus datos personales en cualquier momento contactándonos por correo electrónico.

7. Cambios a esta política
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.
`.trim();
const termsContent = `
Última actualización: febrero 2026

1. Producto digital
Anti-Hinchazón 14 Días (Peri 30+) es un producto digital en formato PDF. Al completar tu compra, recibirás acceso inmediato para descargar el material.

2. Naturaleza educativa
Este producto es una guía educativa e informativa. No constituye consejo médico, diagnóstico ni tratamiento. Consulta siempre con un profesional de la salud antes de realizar cambios en tu alimentación.

3. Política de reembolso
Debido a la naturaleza digital del producto y la entrega inmediata, todas las ventas son finales. Si experimentas problemas técnicos con la descarga, contáctanos y te ayudaremos a resolver el inconveniente.

4. Propiedad intelectual
Todo el contenido del producto está protegido por derechos de autor. No está permitida la reproducción, distribución o reventa del material sin autorización expresa.

5. Uso personal
La licencia de compra es para uso personal e individual. No está permitido compartir, distribuir o revender el contenido.

6. Limitación de responsabilidad
Los resultados pueden variar de persona a persona. No garantizamos resultados específicos. El uso de esta guía es bajo tu propia responsabilidad.

7. Ley aplicable
Estos términos se rigen por las leyes aplicables en la jurisdicción correspondiente.
`.trim();
function LegalModal({
  title,
  content,
  onClose
}: {
  title: string;
  content: string;
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-24">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-charcoal/50 backdrop-blur-sm" data-id="element-25" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 10,
      scale: 0.97
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} onClick={e => e.stopPropagation()} className="relative bg-cream rounded-2xl shadow-2xl shadow-charcoal/15 w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden border border-gold/15" data-id="element-26">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm-dark" data-id="element-27">
          <h3 className="font-sans font-bold text-lg text-charcoal" data-id="element-28">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-charcoal/50 hover:text-charcoal hover:bg-warm transition-colors duration-150" aria-label="Cerrar" data-id="element-29">
            <XIcon className="w-5 h-5" data-id="element-30" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 py-5 flex-1" data-id="element-31">
          <div className="font-serif text-base leading-[1.8] text-charcoal/80 whitespace-pre-line" data-id="element-32">
            {content}
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <>
      <footer className="w-full bg-warm-alt py-8 border-t border-warm-dark" data-id="element-33">
        <div className="max-w-prose mx-auto px-5 flex flex-col items-center gap-3" data-id="element-34">
          <div className="flex items-center gap-4 font-sans text-sm" data-id="element-35">
            <button onClick={() => setActiveModal('privacy')} className="text-charcoal/50 hover:text-terracotta transition-colors duration-150" data-id="element-36">
              Política de Privacidad
            </button>
            <span className="text-charcoal/20" data-id="element-37">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-charcoal/50 hover:text-terracotta transition-colors duration-150" data-id="element-38">
              Términos y Condiciones
            </button>
          </div>
          <p className="font-serif text-xs text-charcoal/35" data-id="element-39">
            © {new Date().getFullYear()} Anti-Hinchazón 14 Días. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-40">
        {activeModal === 'privacy' && <LegalModal title="Política de Privacidad" content={privacyContent} onClose={() => setActiveModal(null)} data-id="element-41" />}
        {activeModal === 'terms' && <LegalModal title="Términos y Condiciones" content={termsContent} onClose={() => setActiveModal(null)} data-id="element-42" />}
      </AnimatePresence>
    </>;
}